import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { getData, isEmpty } from "../../../js/Helpers";
import LoadingOverlay from "../common/LoadingOverlay";
import { useConflictActions, useConflictData, useLoadingActions, useScheduleActions, useScheduleData, useViewData } from "../store/magnetboardStore";
import { useModalsActions, useModalsData } from "../store/modalsStore";
import { useReportsActions, useReportsData } from "../store/reportsStore";

const ReportsMain = lazy(() => import("./ReportsMain"));
const EditSectionModal = lazy(() => import("../modals/edit_section"));
const EditStudentScheduleModal = lazy(() => import("../modals/edit_student_schedule"));

export default function HSMSReportsHost({
  schoolSubscribed,
  schoolInfoLoading,
  schoolInfo,
  scheduleInfoLoading,
  scheduleInfo,
  scheduleStructureDataLoading,
  scheduleStructureData,
  sectionsLoading,
  teachersLoading,
  coursesLoading,
  classroomsLoading,
  studentsLoading,
  userInfo,
  sections,
  subsections,
  setSections,
  setLabels,
  setSubsections,
  students,
  setStudents,
  teachers,
  setTeachers,
  courses,
  setCourses,
  classrooms,
  setClassrooms,
  departments,
  labels,
  lunchInfo,
}) {
  const [viewPreferencesLoaded, setViewPreferencesLoaded] = useState(false);

  const { openReports } = useReportsData();
  const { setScheduleSummaryScope } = useReportsActions();
  const { scheduleType, currentScheduleVersionID, dayOptions } = useScheduleData();
  const { currentViewType, currentView, currentDay } = useViewData();
  const { conflicts } = useConflictData();
  const { showEditStudentScheduleModal, showEditSectionModal, editSectionModalInfo, editStudentScheduleModalInfo } = useModalsData();
  const { setEditSectionModalInfo, toggleShowEditSectionModal, setEditStudentScheduleModalInfo, setEditStudentScheduleModal } = useModalsActions();
  const { initializeSchedule, updatePeriodType, loadFromSessionStorage, setCurrentScheduleVersionID, setCurrentSchedule, setOtherSchedulesVisible } = useScheduleActions();
  const { setConflicts, setConflictsLoading, setMatchPercent, setMatchPercentLoading } = useConflictActions();
  const { setIsLoading } = useLoadingActions();
  const isCounselor = userInfo && `${userInfo.role_id}` === "4";

  const supervisorScheduleSummaryScope = useMemo(() => {
    if(!userInfo || `${userInfo.role_id}` !== "3") return null;

    const user_id = `${userInfo.user_id || ""}`;
    const supervisor_department_ids = departments.reduce((results, department) => {
      const supervisors = Array.isArray(department.supervisors) ? department.supervisors : [];
      if(supervisors.some(supervisor => `${supervisor.user_id || ""}` === user_id)) results.push(department.department_id);
      return results;
    }, []);
    const supervisor_department_id_set = new Set(supervisor_department_ids.map(department_id => `${department_id}`));
    const hasSupervisorDepartment = (department_ids = []) => (
      Array.isArray(department_ids) && department_ids.some(department_id => supervisor_department_id_set.has(`${department_id}`))
    );

    const allowed_course_ids = courses
      .filter(course => hasSupervisorDepartment(course.departments))
      .map(course => course.course_id);
    const allowed_course_id_set = new Set(allowed_course_ids.map(course_id => `${course_id}`));
    const sectionIsSupervisorDepartment = (section) => hasSupervisorDepartment(section?.departments);

    const allowed_teacher_ids = teachers
      .filter(teacher => {
        if(hasSupervisorDepartment(teacher.departments)) return true;
        return sections.some(section =>
          sectionIsSupervisorDepartment(section) &&
          allowed_course_id_set.has(`${section.course_id}`) &&
          (`${section.teacher_id}` === `${teacher.teacher_id}` ||
            (Array.isArray(section.secondary_teachers) && section.secondary_teachers.some(teacher_id => `${teacher_id}` === `${teacher.teacher_id}`)))
        );
      })
      .map(teacher => teacher.teacher_id);

    const allowed_classroom_ids = classrooms
      .filter(classroom => {
        if(hasSupervisorDepartment(classroom.departments)) return true;
        return sections.some(section =>
          sectionIsSupervisorDepartment(section) &&
          `${section.classroom_id || ""}` === `${classroom.classroom_id}`
        );
      })
      .map(classroom => classroom.classroom_id);

    return {
      restrict_to_allowed_ids: true,
      allowed_department_ids: supervisor_department_ids,
      allowed_teacher_ids,
      allowed_course_ids,
      allowed_classroom_ids,
    };
  }, [classrooms, courses, departments, sections, teachers, userInfo]);

  useEffect(() => {
    setScheduleSummaryScope(supervisorScheduleSummaryScope);
  }, [setScheduleSummaryScope, supervisorScheduleSummaryScope]);

  useEffect(() => {
    if(
      !scheduleInfoLoading &&
      !scheduleStructureDataLoading &&
      !schoolInfoLoading &&
      !isEmpty(scheduleInfo) &&
      !isEmpty(schoolInfo)
    ) {
      initializeSchedule(scheduleInfo, schoolInfo, scheduleStructureData);
    }
  }, [
    scheduleInfoLoading,
    scheduleStructureDataLoading,
    schoolInfoLoading,
    scheduleInfo,
    schoolInfo,
    scheduleStructureData,
    initializeSchedule,
  ]);

  useEffect(() => {
    if(scheduleType && currentViewType) {
      updatePeriodType();
    }
  }, [scheduleType, currentViewType, updatePeriodType]);

  useEffect(() => {
    if(schoolInfoLoading || scheduleInfoLoading || viewPreferencesLoaded) return;
    loadFromSessionStorage();
    setViewPreferencesLoaded(true);
  }, [schoolInfoLoading, scheduleInfoLoading, viewPreferencesLoaded, loadFromSessionStorage]);

  const viewTypeOptions = useMemo(() => [
    { value: "period", display: "Period View" },
    { value: "day", display: "Day View" },
  ], []);

  const toggleEditSection = useCallback((passed_data) => {
    let { section_id = null, data_id = null, section_type = null, course_period_id = null } = passed_data || {};

    if(section_id) {
      const subsection = subsections.find((subsection_info) => `${subsection_info.subsection_id}` === `${section_id}`);
      if(subsection) {
        section_id = subsection.section_id;
      }
    }

    setEditSectionModalInfo({
      section_id,
      data_id,
      section_type,
      course_period_id,
    });
    toggleShowEditSectionModal();
  }, [subsections, setEditSectionModalInfo, toggleShowEditSectionModal]);

  const toggleEditStudentSchedule = useCallback((student_id, student_navigation_ids = null) => {
    setEditStudentScheduleModalInfo({ student_id, student_navigation_ids });

    if(!student_id) {
      setEditStudentScheduleModal(false);
    } else if(!showEditStudentScheduleModal) {
      setEditStudentScheduleModal(true);
    }
  }, [setEditStudentScheduleModalInfo, setEditStudentScheduleModal, showEditStudentScheduleModal]);

  const setEditStudentScheduleStudentID = useCallback((student_id) => {
    setEditStudentScheduleModalInfo({ student_id });

    if(!student_id) {
      setEditStudentScheduleModal(false);
    } else if(!showEditStudentScheduleModal) {
      setEditStudentScheduleModal(true);
    }
  }, [setEditStudentScheduleModalInfo, setEditStudentScheduleModal, showEditStudentScheduleModal]);

  const pullNewSectionData = useCallback((schedule_version_id, callback = () => {}) => {
    setOtherSchedulesVisible(false);

    const previous_schedule_index = scheduleInfo.schedules.findIndex(
      (schedule) => `${schedule.schedule_version_id}` === `${currentScheduleVersionID}`
    );
    const next_schedule_index = scheduleInfo.schedules.findIndex(
      (schedule) => `${schedule.schedule_version_id}` === `${schedule_version_id}`
    );

    if(next_schedule_index === -1) return;
    if(previous_schedule_index !== -1) scheduleInfo.schedules[previous_schedule_index].current_bool = "0";

    scheduleInfo.schedules[next_schedule_index].current_bool = "1";

    setCurrentScheduleVersionID(scheduleInfo.schedules[next_schedule_index].schedule_version_id);
    setCurrentSchedule({ ...scheduleInfo.schedules[next_schedule_index] });

    getData("sections", "/get-sections", {
      school_id: schoolInfo.school_id,
      schedule_version_id,
    }, true).then((returned_data) => {
      setLabels(returned_data.labels);
      setSubsections(returned_data.subsections);
      setSections(returned_data.sections);
      setIsLoading(false);
      callback();
    });
  }, [
    currentScheduleVersionID,
    scheduleInfo,
    schoolInfo.school_id,
    setCurrentSchedule,
    setCurrentScheduleVersionID,
    setIsLoading,
    setLabels,
    setOtherSchedulesVisible,
    setSections,
    setSubsections,
  ]);

  return (
    <>
      {openReports.length > 0 && (
        <Suspense fallback={<LoadingOverlay />}>
          <ReportsMain
            scheduleData={{
              schoolInfo,
              schoolSubscribed,
              scheduleInfo,
              scheduleStructureData,
              lunchInfo,
              sections,
              subsections,
              teachers,
              courses,
              students,
              classrooms,
              departments,
              labels,
            }}
            actions={{
              toggleEditStudentSchedule,
              toggleEditSection,
              pullNewSectionData,
              setStudents,
              setSections,
            }}
          />
        </Suspense>
      )}

      {showEditSectionModal && (
        <Suspense fallback={<LoadingOverlay />}>
          <EditSectionModal
            schoolInfo={schoolInfo}
            currentView={currentView}
            currentDay={currentDay}
            currentViewType={currentViewType}
            scheduleType={scheduleType}
            sectionType={editSectionModalInfo.section_type}
            sectionID={editSectionModalInfo.section_id}
            dataID={editSectionModalInfo.data_id}
            coursePeriodID={editSectionModalInfo.course_period_id}
            toggleEditSection={toggleEditSection}
            scheduleInfo={scheduleInfo}
            scheduleStructureData={scheduleStructureData}
            lunchInfo={lunchInfo}
            classroomsLoading={classroomsLoading}
            classrooms={classrooms}
            coursesLoading={coursesLoading}
            courses={courses}
            teachersLoading={teachersLoading}
            teachers={teachers}
            studentsLoading={studentsLoading}
            students={students}
            sectionsLoading={sectionsLoading}
            sections={sections}
            subsections={subsections}
            labels={labels}
            setCourses={setCourses}
            setStudents={setStudents}
            setTeachers={setTeachers}
            setSections={setSections}
            setSubsections={setSubsections}
            setLabels={setLabels}
            conflicts={conflicts}
            setConflicts={setConflicts}
            setConflictsLoading={setConflictsLoading}
            setMatchPercent={setMatchPercent}
            setMatchPercentLoading={setMatchPercentLoading}
            toggleEditStudentSchedule={toggleEditStudentSchedule}
            allow_hotlinks={true}
            studentOnlyEditing={isCounselor}
          />
        </Suspense>
      )}

      {showEditStudentScheduleModal && (
        <Suspense fallback={<LoadingOverlay />}>
          <EditStudentScheduleModal
            schoolInfo={schoolInfo}
            scheduleType={scheduleType}
            currentView={currentView}
            currentViewType={currentViewType}
            currentDay={currentDay}
            studentID={editStudentScheduleModalInfo.student_id}
            studentNavigationIDs={editStudentScheduleModalInfo.student_navigation_ids}
            setStudentID={setEditStudentScheduleStudentID}
            toggleEditStudentSchedule={toggleEditStudentSchedule}
            scheduleInfo={scheduleInfo}
            scheduleStructureData={scheduleStructureData}
            userInfo={userInfo}
            lunchInfo={lunchInfo}
            classrooms={classrooms}
            courses={courses}
            setCourses={setCourses}
            teachers={teachers}
            setTeachers={setTeachers}
            students={students}
            setStudents={setStudents}
            sections={sections}
            setSections={setSections}
            subsections={subsections}
            labels={labels}
            departments={departments}
            viewTypeOptions={viewTypeOptions}
            dayOptions={dayOptions}
            conflicts={conflicts}
            setConflicts={setConflicts}
            setMatchPercent={setMatchPercent}
          />
        </Suspense>
      )}
    </>
  );
}
